import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, combineLatest, concat, defer, fromEvent, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VisibilityService {

  private pageVisible$: Observable<boolean>;

  private eventOptions = { capture: true, passive: true };

  constructor(@Inject(DOCUMENT) document: any) {
    this.pageVisible$ = concat(
      defer(() => of(!document.hidden)),
      fromEvent(document, 'visibilitychange')
        .pipe(map(e => !document.hidden)));
  }

  elementInSight(element: HTMLElement): Observable<boolean> {
    let elementVisible$: Observable<boolean>;

    if (typeof IntersectionObserver === 'undefined') {
      elementVisible$ = fromEvent(window, 'scroll', this.eventOptions as EventListenerOptions).pipe(
        debounceTime(100),
        startWith(0),
        map(() => this.isDOMElementVisible(element)),
        distinctUntilChanged());
    } else {
      elementVisible$ = new Observable<IntersectionObserverEntry[]>(observer => {
        const intersectionObserver = new IntersectionObserver(entries => observer.next(entries));
        intersectionObserver.observe(element);
        return () => { intersectionObserver.disconnect(); };
      })
        .pipe(
          mergeMap(entries => entries),
          map(entry => entry.isIntersecting),
          distinctUntilChanged(),
        );
    }

    const elementInSight$ = combineLatest([
      this.pageVisible$,
      elementVisible$,
    ]).pipe(map(([pageVisible, elementVisible]) => pageVisible && elementVisible), distinctUntilChanged());

    return elementInSight$;
  }

  whenFirstVisible(element: HTMLElement, forceLoad?: Observable<boolean>): Observable<boolean> {
    return combineLatest([this.elementInSight(element), forceLoad || of(false)])
      .pipe(
        map(([visible, force]) => visible || force),
        filter(x => x),
        take(1),
      );
  }

  waitUntilFirstVisible<T>(obs: Observable<T>, element: HTMLElement, forceLoad?: Observable<boolean>) {
    return combineLatest([obs, this.whenFirstVisible(element, forceLoad)]).pipe(map(([value]) => value));
  }

  waitUntilVisibleAfterChange<T>(obs: Observable<T>, element: HTMLElement, forceLoad?: Observable<boolean>) {
    return obs.pipe(
      switchMap(x =>
        this.whenFirstVisible(element, forceLoad).pipe(
          map(() => x),
          startWith(null as T)),
      ));
  }

  isDOMElementVisible(el: HTMLElement, mode: 'full' | 'bottom' | 'any' = 'any') {
    const rect = el.getBoundingClientRect();
    const clw = window.innerWidth || window.document.documentElement.clientWidth;
    const clh = window.innerHeight || window.document.documentElement.clientHeight;

    if (mode === 'full') {
      return (rect.top >= 0 && rect.bottom <= clh) && (rect.left >= 0 && rect.right <= clw);
    }

    if (mode === 'bottom') {
      return rect.bottom >= 0 && rect.bottom <= clh;
    }

    return rect.left <= clw && rect.right >= 0 && rect.top <= clh && rect.bottom >= 0;
  }
}
